import { collectResourcePaths } from './Package';
import { ENGINE_LOGGING_ENABLED } from '@/utils/Constants';
import { isLocalFileContext } from '@/utils/Utilities';

export interface DownloadProgress {
	processed: number;
	total: number | null;
	state: 'idle' | 'collecting' | 'downloading' | 'zipping' | 'success' | 'error' | 'aborted';
	message?: string;
}

type ProgressCallback = (progress: DownloadProgress) => void;

type ResourceEntry = {
	url: string;
	zipPath: string;
	optional?: boolean;
};

let currentProgress: DownloadProgress = {
	processed: 0,
	total: null,
	state: 'idle'
};

let progressCallback: ProgressCallback | null = null;
let abortController: AbortController | null = null;
let downloadActive = false;

export function setDownloadProgressCallback(callback: ProgressCallback | null): void {
	progressCallback = callback;
	if (callback) {
		callback({ ...currentProgress });
	}
}

export function getDownloadProgress(): DownloadProgress {
	return { ...currentProgress };
}

export function abortDownloadPackage(): void {
	if (abortController) {
		abortController.abort();
		abortController = null;
	}
}

function updateProgress(progress: Partial<DownloadProgress>): void {
	currentProgress = { ...currentProgress, ...progress };
	if (progressCallback) {
		progressCallback({ ...currentProgress });
	}
}

function getFflate(): { zipSync: (data: Record<string, Uint8Array>, opts?: any) => Uint8Array; strToU8: (text: string) => Uint8Array } | null {
	const fflate = (window as any).fflate;
	if (!fflate || typeof fflate.zipSync !== 'function' || typeof fflate.strToU8 !== 'function') {
		return null;
	}
	return fflate;
}

function stripQueryAndHash(path: string): string {
	return path.split('#')[0].split('?')[0];
}

function normalizeZipPath(pathname: string): string | null {
	if (!pathname) return null;
	const cleaned = stripQueryAndHash(pathname).replace(/^\.\/+/, '');
	const normalized = cleaned.startsWith('/') ? cleaned.slice(1) : cleaned;
	if (!normalized) return null;
	return normalized;
}

function collectHtmlAssets(html: string, baseDirUrl: string): ResourceEntry[] {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');
	const assets = new Map<string, ResourceEntry>();

	const linkElements = Array.from(doc.querySelectorAll('link[href]'));
	for (const link of linkElements) {
		const rel = (link.getAttribute('rel') || '').toLowerCase();
		if (!rel.includes('stylesheet') && !rel.includes('icon')) continue;
		const href = link.getAttribute('href');
		if (!href) continue;
		const entry = resolveAssetEntry(href, baseDirUrl);
		if (entry) assets.set(entry.zipPath, entry);
	}

	const scriptElements = Array.from(doc.querySelectorAll('script[src]'));
	for (const script of scriptElements) {
		const src = script.getAttribute('src');
		if (!src) continue;
		const entry = resolveAssetEntry(src, baseDirUrl);
		if (entry) assets.set(entry.zipPath, entry);
	}

	return Array.from(assets.values());
}

function isRelativeUrl(path: string): boolean {
	const trimmed = path.trim();
	if (!trimmed) return false;
	if (trimmed.startsWith('//')) return false;
	return !/^[a-z][a-z0-9+.-]*:/i.test(trimmed);
}

function resolveAssetEntry(path: string, baseDirUrl: string): ResourceEntry | null {
	if (!isRelativeUrl(path)) return null;
	let resolved: URL;
	try {
		resolved = new URL(path, baseDirUrl);
	} catch {
		return null;
	}

	if (resolved.protocol === 'data:' || resolved.protocol === 'blob:') return null;
	const baseDir = new URL(baseDirUrl);
	const basePath = baseDir.pathname;
	let zipPath = '';
	if (resolved.pathname.startsWith(basePath)) {
		zipPath = resolved.pathname.slice(basePath.length);
	} else {
		zipPath = resolved.pathname;
	}
	zipPath = normalizeZipPath(zipPath) || '';
	if (!zipPath) return null;

	return { url: resolved.href, zipPath };
}

function resolveZipPathRelativeTo(baseZipPath: string, relativePath: string): string | null {
	const cleanedRelativePath = stripQueryAndHash(relativePath);
	if (!cleanedRelativePath) return null;
	// Split base path into directory parts
	const baseParts = baseZipPath.split('/').filter(p => p);
	const baseDirParts = baseParts.slice(0, -1); // Remove filename, keep directory parts
	
	// Split relative path into parts
	const relParts = cleanedRelativePath.split('/').filter(p => p && p !== '.');
	
	// Resolve .. and . in relative path
	const resolvedParts = [...baseDirParts];
	for (const part of relParts) {
		if (part === '..') {
			if (resolvedParts.length > 0) {
				resolvedParts.pop();
			}
		} else {
			resolvedParts.push(part);
		}
	}
	
	return resolvedParts.join('/');
}

function resolveCssAssetEntry(cssEntry: ResourceEntry, assetPath: string): ResourceEntry | null {
	const cleanedPath = stripQueryAndHash(assetPath.trim());
	if (!cleanedPath || cleanedPath.startsWith('#')) return null;
	if (!isRelativeUrl(cleanedPath)) return null;

	let resolvedUrl: URL;
	try {
		resolvedUrl = new URL(assetPath, cssEntry.url);
	} catch {
		return null;
	}

	if (resolvedUrl.protocol === 'data:' || resolvedUrl.protocol === 'blob:') return null;

	let zipPath: string | null = null;
	if (cleanedPath.startsWith('/')) {
		zipPath = normalizeZipPath(cleanedPath);
	} else {
		zipPath = resolveZipPathRelativeTo(cssEntry.zipPath, cleanedPath);
	}
	if (!zipPath) return null;

	return { url: resolvedUrl.href, zipPath };
}

function collectCssAssetEntries(cssText: string, cssEntry: ResourceEntry): ResourceEntry[] {
	const assets = new Map<string, ResourceEntry>();
	const importRegex = /@import\s+(?:url\(\s*)?(?:['"]?)([^'")\s]+)(?:['"]?)\s*\)?[^;]*;/gi;
	const urlRegex = /url\(\s*(['"]?)(.*?)\1\s*\)/gi;

	let match: RegExpExecArray | null;
	while ((match = importRegex.exec(cssText)) !== null) {
		const importPath = match[1];
		if (!importPath) continue;
		const entry = resolveCssAssetEntry(cssEntry, importPath);
		if (entry) assets.set(entry.zipPath, entry);
	}

	while ((match = urlRegex.exec(cssText)) !== null) {
		const urlPath = match[2];
		if (!urlPath) continue;
		const entry = resolveCssAssetEntry(cssEntry, urlPath);
		if (entry) assets.set(entry.zipPath, entry);
	}

	return Array.from(assets.values());
}

function parseSourceMappingURL(jsContent: string, jsEntry: ResourceEntry, baseDirUrl: string): ResourceEntry | null {
	const sourceMapRegex = /\/\/#\s*sourceMappingURL\s*=\s*([^\s]+)/;
	const match = jsContent.match(sourceMapRegex);
	if (!match || !match[1]) return null;

	const mapPath = match[1].trim();
	if (!isRelativeUrl(mapPath)) return null;

	// Resolve the source map URL relative to the JS file's directory (for fetching)
	const jsUrl = new URL(jsEntry.url);
	const jsDirUrl = new URL('./', jsUrl.href).href;
	let resolvedMapUrl: URL;
	try {
		resolvedMapUrl = new URL(mapPath, jsDirUrl);
	} catch {
		return null;
	}

	if (resolvedMapUrl.protocol === 'data:' || resolvedMapUrl.protocol === 'blob:') return null;

	// Compute zip path relative to the JS file's zip path
	const mapZipPath = resolveZipPathRelativeTo(jsEntry.zipPath, mapPath);
	if (!mapZipPath) return null;

	return {
		url: resolvedMapUrl.href,
		zipPath: mapZipPath,
		optional: true
	};
}

function buildDownloadFileName(): string {
	const rawTitle = document.title || 'regalia-game';
	const sanitized = rawTitle
		.replace(/[\\/:*?"<>|]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
	return (sanitized || 'regalia-game') + '.zip';
}

function buildZipRootFolderName(): string {
	const rawTitle = document.title || 'regalia-game';
	const sanitized = rawTitle
		.replace(/[\\/:*?"<>|]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
	return sanitized || 'regalia-game';
}

async function fetchResource(entry: ResourceEntry, signal: AbortSignal): Promise<Uint8Array | null> {
	try {
		const response = await fetch(entry.url, { signal });
		if (!response.ok) {
			if (entry.optional) {
				return null;
			}
			// Non-optional resource failed. Treat as warning and continue.
			console.warn(`Failed to fetch ${entry.url} (${response.status}) - skipping.`);
			updateProgress({ message: `Warning: Failed to fetch ${entry.url} (${response.status}). Some files may be missing.` });
			return null;
		}
		const buffer = await response.arrayBuffer();
		return new Uint8Array(buffer);
	} catch (err: any) {
		if (entry.optional) return null;
		console.warn(`Failed to fetch ${entry.url} - ${err?.message || err}`);
		updateProgress({ message: `Warning: Failed to fetch ${entry.url} (${err?.message || err}). Some files may be missing.` });
		return null;
	}
}

export async function downloadGamePackage(): Promise<void> {
	if (downloadActive) return;
	if (isLocalFileContext()) {
		updateProgress({ state: 'error', message: 'Download unavailable in local file context.' });
		return;
	}

	const fflate = getFflate();
	if (!fflate) {
		updateProgress({ state: 'error', message: 'Compression library unavailable.' });
		return;
	}

	downloadActive = true;
	abortController = new AbortController();
	currentProgress = { processed: 0, total: null, state: 'collecting' };

	try {
		updateProgress({ state: 'collecting', message: 'Fetching page HTML...' });
		const pageUrl = window.location.href;
		const baseDirUrl = new URL('./', pageUrl).href;
		const htmlResponse = await fetch(pageUrl, { signal: abortController.signal, cache: 'no-cache' });
		if (!htmlResponse.ok) {
			throw new Error(`Failed to fetch HTML (${htmlResponse.status})`);
		}
		const htmlText = await htmlResponse.text();
		const htmlAssets = collectHtmlAssets(htmlText, baseDirUrl);

		const resourcePaths = collectResourcePaths();
		const compassDirections = ['NorthWest', 'North', 'NorthEast', 'East', 'SouthEast', 'South', 'SouthWest', 'West'];
		const engineImagePaths = [
			'300px-Compass_Rose_English_North.png',
			'300px-Compass_Rose_UD.png',
			...compassDirections.map(dir => `${dir}.png`)
		];
		const allImagePaths = new Set(resourcePaths);
		engineImagePaths.forEach((path) => allImagePaths.add(path));

		const mediaAssets: ResourceEntry[] = [];
		for (const path of allImagePaths) {
			const url = new URL(`images/${path}`, baseDirUrl).href;
			mediaAssets.push({ url, zipPath: `images/${path}` });
		}

		const extraAssets = new Map<string, ResourceEntry>();
		const workerEntry = resolveAssetEntry('regalia/worker.js', baseDirUrl);
		if (workerEntry) {
			extraAssets.set(workerEntry.zipPath, workerEntry);
		}

		const sourceMapAssets = new Map<string, ResourceEntry>();

		const zipRoot = buildZipRootFolderName();
		const files: Record<string, Uint8Array> = {};
		files[`${zipRoot}/index.html`] = fflate.strToU8(htmlText);
		let processed = 1;
		updateProgress({ processed, message: 'Added index.html' });

		let totalFiles = 1;
		const pendingAssets: ResourceEntry[] = [];
		const seenAssets = new Map<string, ResourceEntry>();

		const enqueueAsset = (entry: ResourceEntry | null): void => {
			if (!entry) return;
			const zipPath = `${zipRoot}/${entry.zipPath}`;
			if (files[zipPath]) return;
			if (seenAssets.has(zipPath)) return;
			seenAssets.set(zipPath, entry);
			pendingAssets.push(entry);
			totalFiles += 1;
			updateProgress({ total: totalFiles });
		};

		for (const entry of [...htmlAssets, ...mediaAssets, ...extraAssets.values()]) {
			enqueueAsset(entry);
		}

		updateProgress({
			state: 'downloading',
			processed,
			total: totalFiles,
			message: 'Preparing files...'
		});

		while (pendingAssets.length > 0) {
			const entry = pendingAssets.shift();
			if (!entry) break;
			if (abortController.signal.aborted) {
				updateProgress({ state: 'aborted', message: 'Download cancelled.' });
				return;
			}

			updateProgress({ message: `Fetching ${entry.zipPath}...` });
			const data = await fetchResource(entry, abortController.signal);
			if (data === null) {
				if (currentProgress.total !== null) {
					updateProgress({ total: Math.max(0, currentProgress.total - 1) });
				}
				continue;
			}
			const zipPath = `${zipRoot}/${entry.zipPath}`;
			files[zipPath] = data;

			if (entry.zipPath.endsWith('.css')) {
				const cssText = new TextDecoder().decode(data);
				const cssAssets = collectCssAssetEntries(cssText, entry);
				for (const cssAsset of cssAssets) {
					enqueueAsset(cssAsset);
				}
			}

			// Parse JavaScript files for source maps
			if (entry.zipPath.endsWith('.js')) {
				const jsText = new TextDecoder().decode(data);
				const mapEntry = parseSourceMappingURL(jsText, entry, baseDirUrl);
				if (mapEntry && !sourceMapAssets.has(mapEntry.zipPath) && !files[`${zipRoot}/${mapEntry.zipPath}`]) {
					sourceMapAssets.set(mapEntry.zipPath, mapEntry);
					enqueueAsset(mapEntry);
				}
			}

			processed += 1;
			updateProgress({ processed });
		}

		updateProgress({ state: 'zipping', message: 'Creating zip archive...' });
		const zipBytes = fflate.zipSync(files, { level: 6 });
		if (abortController.signal.aborted) {
			updateProgress({ state: 'aborted', message: 'Download cancelled.' });
			return;
		}

		const blob = new Blob([zipBytes as Uint8Array<ArrayBuffer>], { type: 'application/zip' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = buildDownloadFileName();
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.setTimeout(() => URL.revokeObjectURL(url), 1000);

		updateProgress({ state: 'success', message: 'Download ready.' });
	} catch (err: any) {
		if (abortController?.signal.aborted) {
			updateProgress({ state: 'aborted', message: 'Download cancelled.' });
		} else {
			if (ENGINE_LOGGING_ENABLED) {
				console.error('Download failed:', err);
			}
			updateProgress({
				state: 'error',
				message: err?.message || 'Download failed.'
			});
		}
	} finally {
		downloadActive = false;
		abortController = null;
	}
}
